import React from 'react';
import {useTranslation} from 'react-i18next';
import {inAnteprima} from '../../skin';
import {giochi as caricaGiochi} from '../../services/casinoApi';
import Copertina, {tono} from './Copertina';
import {sezioneDelCatalogo} from './comune';

/** Quanti giochi propone «Prova anche». */
const QUANTI = 3;

/**
 * IL GIOCO NON E' PARTITO: un riquadro al centro della cornice del gioco,
 * sopra i colori del gioco stesso, con un titolo che dice cosa e' successo,
 * una frase su cosa fare, i pulsanti giusti per quel caso e, sotto,
 * «Prova anche»: tre giochi da aprire al suo posto. Il codice sta in piccolo
 * in fondo, per chi chiama l'assistenza; il dettaglio del servizio solo
 * nell'anteprima del backoffice.
 *
 * QUALI GIOCHI PROPONE, dall'elenco del fornitore della stessa sezione e mai
 * quello che non e' partito: prima quelli della stessa categoria, poi dello
 * stesso fornitore, poi i primi della lobby. Se l'elenco non arriva (il
 * casino' non risponde) «Prova anche» non c'e'.
 */
export default function ErroreGioco({errore, gioco, sezione, provaPossibile, onRiprova, onAccedi, onProva, onLobby, onApri, onVersa}) {
  const {t} = useTranslation();
  const [consigliati, setConsigliati] = React.useState([]);

  React.useEffect(() => {
    let vivo = true;
    caricaGiochi(sezioneDelCatalogo(sezione))
        .then((elenco) => vivo && setConsigliati(scegliConsigliati(elenco, gioco)))
        .catch(() => vivo && setConsigliati([]));
    return () => { vivo = false; };
  }, [sezione, gioco]);

  const testi = {
    nonDisponibile: {icona: '🎰', titolo: t('Questo gioco non è disponibile'),
      testo: t('Il gioco è stato tolto o è in manutenzione. Scegline un altro: ce ne sono tanti nella lobby.')},
    nonRisponde: {icona: '📡', titolo: t('Il casinò non risponde'),
      testo: t('Non riusciamo a collegarci al gioco in questo momento. Riprova tra qualche secondo.')},
    accesso: {icona: '🔒', titolo: t('Accedi per giocare'),
      testo: t('Per giocare con il saldo vero serve il tuo conto.')},
    sessione: {icona: '⏱️', titolo: t('La sessione è scaduta'),
      testo: t('Per sicurezza ti abbiamo disconnesso. Rientra per riprendere a giocare.')},
    prova: {icona: '🎁', titolo: t('Prova gratuita non disponibile'),
      testo: t('Questo gioco non ha la prova gratuita. Accedi per giocare con il tuo saldo.')},
    credito: {icona: '💳', titolo: t('Non hai credito per giocare'),
      testo: provaPossibile
        ? t('Sul tuo conto non c’è credito. Fai un versamento per giocare con il saldo vero, oppure prova il gioco gratis.')
        : t('Sul tuo conto non c’è credito. Fai un versamento per giocare con il saldo vero.')},
    livello: {icona: '🚫', titolo: t('Il tuo conto non gioca al casinò'),
      testo: t('Questo conto non può giocare con il saldo vero. Scrivi alla tua assistenza per farlo abilitare.')},
    valuta: {icona: '💱', titolo: t('Gioco non disponibile nella tua valuta'),
      testo: t('Il fornitore non offre questo gioco nella valuta del tuo conto. Scegline un altro dalla lobby.')},
    altro: {icona: '⚠️', titolo: t('Il gioco non è partito'),
      testo: t('Qualcosa non ha funzionato. Riprova tra poco.')},
  };
  const {icona, titolo, testo} = testi[errore.tipo] || testi.altro;

  const principale = (etichetta, f) => <button type={'button'} className={'cas-errore-btn'} onClick={f}>{etichetta}</button>;
  const secondario = (etichetta, f) => <button type={'button'} className={'cas-errore-btn secondario'} onClick={f}>{etichetta}</button>;
  const pulsanti = {
    nonDisponibile: [principale(t('Torna ai giochi'), onLobby)],
    nonRisponde: [principale(t('Riprova'), onRiprova), secondario(t('Torna ai giochi'), onLobby)],
    accesso: [principale(t('Accedi'), onAccedi), provaPossibile && secondario(t('Prova gratis'), onProva)],
    sessione: [principale(t('Accedi di nuovo'), onAccedi)],
    prova: [principale(t('Accedi'), onAccedi), secondario(t('Torna ai giochi'), onLobby)],
    // «Versa» solo se la pagina lo passa: chi non ha la voce del versamento non lo vede.
    credito: [!!onVersa && principale(t('Versa'), onVersa), provaPossibile && secondario(t('Prova gratis'), onProva), secondario(t('Torna ai giochi'), onLobby)],
    livello: [principale(t('Torna ai giochi'), onLobby)],
    valuta: [principale(t('Torna ai giochi'), onLobby)],
    altro: [principale(t('Riprova'), onRiprova), secondario(t('Torna ai giochi'), onLobby)],
  }[errore.tipo] || [];

  const fondo = gioco && gioco.copertina ? {backgroundImage: 'url("' + gioco.copertina.replace(/"/g, '%22') + '")'} : undefined;
  return (
    <div className={'cas-cornice cas-errore'} style={{'--h': tono(gioco ? gioco.id : '')}}>
      <span className={'cas-errore-fondo' + (fondo ? ' con-immagine' : '')} style={fondo} aria-hidden={'true'}/>
      <div className={'cas-errore-riquadro'} role={'alert'}>
        <span className={'cas-errore-icona'} aria-hidden={'true'}>{icona}</span>
        <h2>{titolo}</h2>
        <p>{testo}</p>
        <div className={'cas-errore-azioni'}>
          {pulsanti.filter(Boolean).map((b, i) => <React.Fragment key={i}>{b}</React.Fragment>)}
        </div>
        {/* Senza credito o con un conto che non gioca, gli altri giochi non partirebbero comunque. */}
        {consigliati.length > 0 && errore.tipo !== 'credito' && errore.tipo !== 'livello' && (
          <div className={'cas-errore-altri'}>
            <p>{t('Prova anche')}</p>
            <div className={'cas-errore-griglia'}>
              {consigliati.map((g) => <Copertina key={g.id} gioco={g} onApri={() => onApri(g)}/>)}
            </div>
          </div>
        )}
        <small className={'cas-errore-codice'}>
          {errore.tipo === 'credito' || errore.tipo === 'livello' ? null : errore.codice
            ? t('Codice {{codice}} · se il problema continua scrivi all’assistenza', {codice: errore.codice})
            : t('Se il problema continua scrivi all’assistenza')}
          {inAnteprima() && errore.tecnico ? <><br/>{errore.tecnico}</> : null}
        </small>
      </div>
    </div>
  );
}

/** I giochi da proporre al posto di quello che non e' partito: stessa categoria, stesso fornitore, poi i primi. */
export function scegliConsigliati(elenco, gioco, quanti = QUANTI) {
  const altri = (elenco || []).filter((g) => !gioco || g.id !== gioco.id);
  const categorie = gioco ? gioco.categorie || [] : [];
  const scelti = [];
  const aggiungi = (g) => { if (scelti.length < quanti && !scelti.includes(g)) scelti.push(g); };
  altri.filter((g) => g.categorie.some((c) => categorie.includes(c))).forEach(aggiungi);
  if (gioco && gioco.fornitore) altri.filter((g) => g.fornitore === gioco.fornitore).forEach(aggiungi);
  altri.forEach(aggiungi);
  return scelti;
}
